/*
 * PDP panel redesign (Oct 2026): keeps the add-to-cart button label as "Add to cart · $X".
 * X = (subscription price if a Recharge selling plan is selected, else variant price) × quantity.
 * Step 3: also re-prices the size cards / unit-price line; step 4: drives the sticky bar.
 *
 * theme.js rewrites the button's innerHTML on every variant change and then dispatches
 * `variant:changed`, so we re-apply the label after that event. Recharge writes the plan id
 * into the form's hidden `selling_plan` input without an event we can rely on, so we also
 * re-check (deferred) after any change/click inside the form.
 */
(function () {
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2).split('.');
    var withCommas = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withPeriods = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    // The store format renders "$ 45.77"; the panel price shows "$45.77", so drop the gap.
    return format.replace(/\$\s+\{\{/, '${{').replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals': return withCommas;
        case 'amount_with_comma_separator': return withPeriods + ',' + value[1];
        case 'amount_no_decimals_with_comma_separator': return withPeriods;
        default: return withCommas + '.' + value[1];
      }
    });
  }

  function init(section) {
    var form = section.querySelector('form.product-form');
    var button = form && form.querySelector('[data-pdp-atc]');
    var jsonEl = section.querySelector('[data-product-json]');
    if (!button || !jsonEl) return;

    var product;
    try {
      product = JSON.parse(jsonEl.textContent).product;
    } catch (e) {
      return;
    }

    function currentVariant() {
      var idInput = form.querySelector('[name="id"]');
      var id = idInput ? parseInt(idInput.value, 10) : NaN;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === id) return product.variants[i];
      }
      return null;
    }

    // When our toggle exists it is the source of truth (see syncPurchase); otherwise fall back to
    // whatever a third-party widget wrote into the form.
    var chosenPlanId = null;

    // {qty, unit} per variant from snippets/pdp-variant-unit.liquid, e.g. {qty: 60, unit: 'capsule'}
    var unitsByVariant = {};
    var unitsEl = section.querySelector('[data-pdp-units]');
    if (unitsEl) {
      try {
        unitsByVariant = JSON.parse(unitsEl.textContent);
      } catch (e) {}
    }

    // "$0.76 / capsule", or '' when the variant has no unit basis
    function perUnit(cents, variant) {
      var basis = variant && unitsByVariant[variant.id];
      if (!basis || !basis.qty) return '';
      var per = Math.round(cents / basis.qty);
      return per > 0 ? formatMoney(per) + ' / ' + basis.unit : '';
    }

    function unitPrice(variant) {
      var planInput = form.querySelector('[name="selling_plan"]');
      var planId = purchase
        ? chosenPlanId
        : (planInput && planInput.value ? parseInt(planInput.value, 10) : null);
      if (planId && variant.selling_plan_allocations) {
        for (var i = 0; i < variant.selling_plan_allocations.length; i++) {
          if (variant.selling_plan_allocations[i].selling_plan_id === planId) {
            return variant.selling_plan_allocations[i].price;
          }
        }
      }
      return variant.price;
    }

    // Step 3: size cards show the active price (Autoship price when a plan is chosen, with the
    // one-time price struck through) and its price per serving. Prices hidden for gated products.
    var sizes = section.querySelector('.pdp-sizes:not([data-pdp-gated])');

    function updateCards() {
      if (!sizes) return;

      sizes.querySelectorAll('[data-pdp-size-card]').forEach(function (card) {
        var id = parseInt(card.getAttribute('data-variant-id'), 10);
        var variant = product.variants.filter(function (v) { return v.id === id; })[0];
        if (!variant) return;

        var price = unitPrice(variant);
        var compare = price < variant.price ? variant.price : variant.compare_at_price;
        var priceEl = card.querySelector('[data-pdp-card-price]');
        var compareEl = card.querySelector('[data-pdp-card-compare]');
        var perEl = card.querySelector('[data-pdp-card-per]');

        if (priceEl) priceEl.innerHTML = formatMoney(price);
        if (compareEl) compareEl.innerHTML = compare > price ? formatMoney(compare) : '';
        if (perEl) perEl.innerHTML = perUnit(price, variant);
      });
    }

    // Price row (products without size cards). theme.js only renders the one-time price on variant
    // change; show the Autoship price with the one-time price struck through when a plan is chosen.
    var priceList = section.querySelector('.product-form__info-item--price:not(.pdp-price--in-cards) .price-list');

    function updatePriceRow(variant) {
      if (!priceList) return;

      var price = unitPrice(variant);
      var compare = price < variant.price ? variant.price : variant.compare_at_price;
      var labels = window.languages || {};

      if (compare > price) {
        priceList.innerHTML =
          '<span class="price price--highlight"><span class="visually-hidden">' + (labels.productSalePrice || '') + '</span>' + formatMoney(price) + '</span>' +
          '<span class="price price--compare"><span class="visually-hidden">' + (labels.productRegularPrice || '') + '</span>' + formatMoney(compare) + '</span>';
      } else {
        priceList.innerHTML = '<span class="price"><span class="visually-hidden">' + (labels.productSalePrice || '') + '</span>' + formatMoney(price) + '</span>';
      }
    }

    // Price row unit-price line (products without size cards).
    var perUnitRow = section.querySelector('[data-pdp-per-unit]');

    function updatePerUnit(variant) {
      if (!perUnitRow) return;
      var text = perUnit(unitPrice(variant), variant);
      perUnitRow.hidden = !text;
      perUnitRow.innerHTML = text;
    }

    // Step 4: sticky bar. Its button clicks the panel's button so the main form is what's added.
    var sticky = section.querySelector('[data-pdp-sticky]');
    var stickyWrapper = sticky && sticky.closest('animate-sticky');

    function updateSticky(variant, qty) {
      if (!sticky) return;

      var stickyButton = sticky.querySelector('[data-pdp-sticky-atc]');
      var available = variant && variant.available && !button.hasAttribute('disabled');
      stickyButton.disabled = !available;
      stickyButton.innerHTML = available
        ? stickyButton.getAttribute('data-label')
        : ((window.languages && window.languages.productFormSoldOut) || 'Sold out');

      if (!variant) return;

      var price = unitPrice(variant);
      var meta = [];
      if (perUnit(price, variant)) meta.push(perUnit(price, variant));
      if (purchase) meta.push(chosenPlanId ? 'Autoship' : 'One-time');

      sticky.querySelector('[data-pdp-sticky-price]').innerHTML = formatMoney(price * qty);
      sticky.querySelector('[data-pdp-sticky-meta]').innerHTML = meta.join(' · ');

      var variantEl = sticky.querySelector('[data-pdp-sticky-variant]');
      if (variantEl) variantEl.textContent = variant.title;
    }

    // Keep page content (footer, chat bubble) clear of the bar while it is showing.
    function syncStickyOffset() {
      if (!stickyWrapper) return;

      var bar = stickyWrapper.querySelector('.product-sticky-cart');
      var shown = bar && parseFloat(getComputedStyle(bar).opacity) > 0.5 &&
        getComputedStyle(bar).pointerEvents !== 'none';
      var height = shown ? sticky.offsetHeight : 0;
      document.body.style.paddingBottom = height ? height + 'px' : '';

      // Shopify Inbox bubble (<shopify-chat>, closed shadow DOM) is lifted by CSS using this offset
      var root = document.documentElement;
      root.classList.toggle('pdp-sticky-shown', !!height);
      root.style.setProperty('--pdp-sticky-height', height + 'px');
    }

    if (sticky) {
      sticky.querySelector('[data-pdp-sticky-atc]').addEventListener('click', function () {
        if (!button.disabled) button.click();
      });

      var offsetPending = false;
      var queueOffset = function () {
        if (offsetPending) return;
        offsetPending = true;
        // after animate-sticky's rAF toggle and the 250ms+250ms reveal transition
        setTimeout(function () {
          offsetPending = false;
          syncStickyOffset();
        }, 600);
      };
      window.addEventListener('scroll', queueOffset, { passive: true });
      window.addEventListener('resize', queueOffset);
      queueOffset();
    }

    function update() {
      var variant = currentVariant();
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      updateCards();
      updateSticky(variant, qty);
      if (variant) {
        updatePriceRow(variant);
        updatePerUnit(variant);
      }

      if (!variant || !variant.available || button.hasAttribute('disabled')) return;

      var label = (window.languages && window.languages.productFormAddToCart) || 'Add to cart';
      button.innerHTML = label + ' · ' + formatMoney(unitPrice(variant) * qty);
    }

    function updateSoon() {
      setTimeout(update, 50);
    }

    /*
     * Step 2: One-time / Autoship toggle (snippets/pdp-purchase-options.liquid). Writes the chosen
     * plan id into the form's `selling_plan` input (empty = one-time). Plans the current variant
     * has no allocation for are disabled; if it has none at all, Autoship is disabled too.
     */
    var purchase = section.querySelector('[data-pdp-purchase]');

    function syncPurchase() {
      if (!purchase) return;

      var variant = currentVariant();
      var planSelect = purchase.querySelector('[data-pdp-plan]');
      var frequency = purchase.querySelector('[data-pdp-frequency]');
      var autoshipRadio = purchase.querySelector('[data-pdp-purchase-mode][value="autoship"]');
      var onetimeRadio = purchase.querySelector('[data-pdp-purchase-mode][value="onetime"]');

      var allowed = {};
      var anyAllowed = false;
      ((variant && variant.selling_plan_allocations) || []).forEach(function (allocation) {
        allowed[allocation.selling_plan_id] = true;
        anyAllowed = true;
      });

      Array.prototype.forEach.call(planSelect.options, function (option) {
        option.disabled = !allowed[parseInt(option.value, 10)];
      });
      if (planSelect.selectedOptions[0] && planSelect.selectedOptions[0].disabled) {
        var firstEnabled = Array.prototype.find.call(planSelect.options, function (o) { return !o.disabled; });
        if (firstEnabled) planSelect.value = firstEnabled.value;
      }

      autoshipRadio.disabled = !anyAllowed;
      if (!anyAllowed && autoshipRadio.checked && onetimeRadio) onetimeRadio.checked = true;

      var autoship = autoshipRadio.checked && anyAllowed;
      frequency.hidden = !autoship;
      chosenPlanId = autoship ? parseInt(planSelect.value, 10) : null;
      purchase.classList.toggle('pdp-purchase--autoship', autoship);

      // Recharge's app embed still auto-injects its (CSS-hidden) widget, which shares/creates a
      // `selling_plan` input and can reset it on variant change. Write every one of them.
      form.querySelectorAll('[name="selling_plan"]').forEach(function (input) {
        input.value = chosenPlanId ? String(chosenPlanId) : '';
      });
    }

    if (purchase) {
      purchase.addEventListener('change', function () {
        syncPurchase();
        update();
      });
      section.addEventListener('variant:changed', syncPurchase);

      // Re-assert our plan right before the form is read: capture phase runs before theme.js's
      // delegated add-to-cart click handler and before native submit.
      section.addEventListener('click', function (event) {
        if (event.target.closest('[data-pdp-atc]')) syncPurchase();
      }, true);
      form.addEventListener('submit', syncPurchase, true);

      syncPurchase();
    }

    section.addEventListener('variant:changed', update);
    form.addEventListener('change', updateSoon);
    form.addEventListener('input', updateSoon);
    form.addEventListener('click', updateSoon);
    update();
  }

  function initAll() {
    document.querySelectorAll('[data-section-type="product"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
